import { injectSvgs } from "./svg-injector";
import {
  GlassRoofSchematic,
  ProductConfiguratorState,
  Step,
} from "./product-configurator";
import {
  disableInputs,
  enableInputs,
  getFormValues,
  sendForm,
  validateForm,
} from "./lib-2sxc-forms";
import { showAlert, showConfigWarnings } from "./lib-2sxc-alerts";
import { addTrackingEvent } from "./lib-2sxc-tracking";

const debug = true;

// so it can be called from the HTML when content re-initializes dynamically
const winAny = window as any;
winAny.appContent = winAny.appContent || {};
winAny.appProductConfigurator ??= {};
winAny.appProductConfigurator.init ??= initProductConfigurator;

export function initProductConfigurator({
  domAttribute,
}: {
  domAttribute: string;
}) {
  if (document.getElementsByTagName("form").length)
    document.getElementsByTagName("form")[0].setAttribute("novalidate", "");

  injectSvgs("inject-svg");

  const schematicInset = {
    widthDimensioning: ["Pfeil_breite"],
    depthDimensioning: ["Pfeil_tiefe"],

    pillarInset: ["Stütze_versetzt_Basisträger"],
    pillarStandard: ["Stütze_normal"],

    topCloth: ["Beschattung_Dach"],
    underGlassCloth: ["Beschattung_Dach_unter_Glas"],

    westGlass: ["Glas_links_Seite_Stützen_versetzt"],
    southGlass: [""],
    eastGlass: ["Glas_Seite_Stützen_versetzt"],

    westCloth: ["Beschattung_links_Stüzen_versetzt"],
    southCloth: [""],
    eastCloth: ["Beschattung_rechts_Stützen_versetzt"],

    illuminationStripes: ["Licht_Streifen_Stütze_versetzt"],
    illuminationStripesWithUnderGlassCloth: [""],
    illuminationSpots: ["Licht_Einzeln_klein_neu"],
    illuminationNone: [
      "Licht_Streifen_Beschattung_unter_Glas",
      "Licht_Streifen_Stütze_versetzt",
      "Licht_Streifen",
      "Licht_Einzeln_klein_neu",
      "Licht_Streifen",
    ],
  } as GlassRoofSchematic;

  const schematicStandard = {
    widthDimensioning: ["Pfeil_breite"],
    depthDimensioning: ["Pfeil_tiefe"],

    pillarInset: ["Stütze_versetzt_Basisträger"],
    pillarStandard: ["Stütze_normal"],

    topCloth: ["Beschattung_Dach"],
    underGlassCloth: ["Beschattung_Dach_unter_Glas"],

    westGlass: ["Glas_linke_Seite_Stütze_normal"],
    southGlass: ["Glas_Front_Stützen_normal"],
    eastGlass: ["Glas_Seite_rechts_normal"],

    westCloth: ["Beschattung_Seite_links_Stützen_normal"],
    southCloth: ["Beschattung_Front"],
    eastCloth: ["Beschattung_Seite_rechts"],

    illuminationStripes: ["Licht_Streifen"],
    illuminationStripesWithUnderGlassCloth: [
      "Licht_Streifen_Beschattung_unter_Glas",
    ],
    illuminationSpots: ["Licht_Einzeln_klein_neu"],
    illuminationNone: [
      "Licht_Streifen_Beschattung_unter_Glas",
      "Licht_Streifen_Stütze_versetzt",
      "Licht_Streifen",
      "Licht_Einzeln_klein_neu",
      "Licht_Streifen",
    ],
  } as GlassRoofSchematic;

  const pcState = new ProductConfiguratorState(schematicStandard);
  const pcWrapper = document.querySelectorAll(`[${domAttribute}]`)[0];

  if (!pcWrapper) return;

  // bind click event for all next buttons
  pcWrapper.querySelectorAll("[app-pc-step-next]").forEach((e) => {
    e.addEventListener("click", (event: Event) => {
      event.preventDefault();

      const currentStep = pcState.getActiveStep();
      const nextStep = +e.getAttribute("data-goto-step") ?? 0;

      let skipValidation = !!e.getAttribute("data-skip-validation");
      if (currentStep.id > nextStep) skipValidation = true; // going backwards never requires validation

      if (!skipValidation && !currentStep.isValid()) return;

      if (pcState.gotoStep(nextStep)) {
        if (nextStep == 3) {
          pcWrapper.classList.add("night-time");
        } else {
          pcWrapper.classList.remove("night-time");
        }

        if (nextStep == 5) {
          // TODO: move this to form submit
          pcWrapper.querySelectorAll(".complete").forEach((f) => {
            f.removeAttribute("hidden");

            setTimeout(() => {
              f.querySelectorAll("svg").forEach((f1) => {
                f1.classList.add("active");
              });
            }, 25);
          });

          pcWrapper.querySelectorAll(".stepper").forEach((f) => {
            f.setAttribute("hidden", "");
          });
        }
      }
    });
  });

  // add steps from DOM to ProductConfiguratorState
  pcWrapper.querySelectorAll("[app-pc-step-content]").forEach((v, k, ary) => {
    const step: Step = new Step();
    step.contentElementRef = v as HTMLElement;
    step.id = +step.contentElementRef.getAttribute("data-step");
    step.showOnFirstActivation = step.contentElementRef.getAttribute(
      "data-show-on-first-activation"
    );

    pcState.addStep(step);
  });

  // stepper icons to step content
  pcWrapper.querySelectorAll("[app-pc-step-icon]").forEach((v, k, ary) => {
    const iconElementRef = v as HTMLElement;
    const stepId = +iconElementRef.getAttribute("data-step");

    pcState.addIconToStep(stepId, iconElementRef);
  });

  // bind schematic state change events to inputs
  pcWrapper.querySelectorAll("[app-pc-input]").forEach((e) => {
    e.addEventListener("focus", (event: Event) => {
      event.preventDefault();
      pcState.triggerOnFocus(e);
    });

    e.addEventListener("blur", (event: Event) => {
      event.preventDefault();
      pcState.triggerOnFocus(e, true);
    });

    e.addEventListener("change", (event: Event) => {
      event.preventDefault();

      if (e instanceof HTMLSelectElement) {
        Array.from(e.options).forEach((f) => {
          pcState.triggerHideOnSelect(f);
        });

        Array.from(e.selectedOptions).forEach((f) => {
          pcState.triggerShowOnSelect(f);
        });

        if (e.id === "shadingTop") {
          if (e.value === "Unterglasmarkise") {
            pcWrapper.classList.add("under-glass-shading");
          } else {
            pcWrapper.classList.remove("under-glass-shading");
          }

          // reset illumintaion
          pcWrapper
            .querySelectorAll("input[name='illumination']")
            .forEach((e) => {
              if (e instanceof HTMLInputElement) {
                pcState.triggerHideOnSelect(e);

                e.checked = false;
                if (e.value === "none") {
                  e.checked = true;
                }
              }
            });
        }
      } else if (e instanceof HTMLInputElement) {
        switch (e.type) {
          case "checkbox":
            e.checked
              ? pcState.triggerShowOnCheck(e)
              : pcState.triggerHideOnUncheck(e);
            break;
          case "radio":
            // hide all elements in this radio button group
            pcWrapper
              .querySelectorAll("input[name='" + e.name + "']")
              .forEach((f) => {
                pcState.triggerHideOnSelect(f);
              });

            if (e.name == "pillarPosition") {
              pcWrapper.classList.remove("under-glass-shading");

              // remove top shading
              pcWrapper.querySelectorAll("#shadingTop").forEach((e) => {
                if (e instanceof HTMLSelectElement) {
                  e.value = "";
                }
              });

              // reset glazing and shadowing checkboxes to false
              pcWrapper
                .querySelectorAll(
                  ".checkbox-container > input[type='checkbox']"
                )
                .forEach((e) => {
                  if (e instanceof HTMLInputElement) {
                    e.checked = false;
                    e.parentElement.removeAttribute("active");
                  }
                });

              // reset illumintaion
              pcWrapper
                .querySelectorAll("input[name='illumination']")
                .forEach((e) => {
                  if (e instanceof HTMLInputElement) {
                    e.checked = false;
                    if (e.value === "none") {
                      e.checked = true;
                    }
                  }
                });

              switch (e.value) {
                case "standard":
                  pcWrapper.classList.remove("inset-pillar");
                  pcState.changeSchematic(schematicStandard, [
                    "westGlass",
                    "southGlass",
                    "eastGlass",
                    "westCloth",
                    "southCloth",
                    "eastCloth",
                    "topCloth",
                    "underGlassCloth",
                    "illuminationStripes",
                    "illuminationStripesWithUnderGlassCloth",
                    "illuminationSpots",
                    "illuminationNone",
                  ]);
                  break;
                case "inset":
                  pcWrapper.classList.add("inset-pillar");
                  pcState.changeSchematic(schematicInset, [
                    "westGlass",
                    "southGlass",
                    "eastGlass",
                    "westCloth",
                    "southCloth",
                    "eastCloth",
                    "topCloth",
                    "underGlassCloth",
                    "illuminationStripes",
                    "illuminationStripesWithUnderGlassCloth",
                    "illuminationSpots",
                    "illuminationNone",
                  ]);
                  break;
              }
            }

            // show selected element
            pcState.triggerShowOnSelect(e);
            break;
        }
      }
    });
  });

  // add active state to checkbox-container
  pcWrapper
    .querySelectorAll(".checkbox-container > input[type='checkbox']")
    .forEach((e) => {
      e.addEventListener("change", (event: Event) => {
        if (e instanceof HTMLInputElement) {
          if (e.checked) e.parentElement.setAttribute("active", null);
          else e.parentElement.removeAttribute("active");
        }
      });
    });

  // show fields for postbox address
  pcWrapper.querySelectorAll("#sameLocation").forEach((e) => {
    e.addEventListener("change", (event: Event) => {
      const postboxContainerElement =
        pcWrapper.querySelector("#postbox-container");

      if (e instanceof HTMLInputElement) {
        if (e.checked) {
          postboxContainerElement.setAttribute("hidden", null);
          postboxContainerElement
            .querySelectorAll("input, label")
            .forEach((f) => {
              f.removeAttribute("required");
            });
        } else {
          postboxContainerElement.removeAttribute("hidden");
          postboxContainerElement
            .querySelectorAll("input, label")
            .forEach((f) => {
              f.setAttribute("required", null);
            });
        }
      }
    });
  });

  pcWrapper.querySelectorAll("[app-pc-send-form]").forEach((e) => {
    e.addEventListener("click", async (event: Event) => {
      const valid = validateForm(pcWrapper);
      if (!valid) return;

      const submitButtom = e as HTMLButtonElement;
      const formValues = await getFormValues(pcWrapper);
      const endpoint = (pcWrapper as HTMLElement).dataset.webservice; // (should be "Form/ProcessForm" or a custom override)

      sendForm(formValues, submitButtom, endpoint).then((result: any) => {
        // error
        if (!result.ok) {
          if (debug) console.log("error", result.status);
          enableInputs(pcWrapper);

          addTrackingEvent(
            "trackProductConfiguratorForm",
            "pc-form",
            submitButtom.innerText
          );
          return;
        }

        // success
        if (debug) console.log("success", result);
        submitButtom.setAttribute("disabled", "");
        disableInputs(pcWrapper, false);

        addTrackingEvent(
          "trackProductConfiguratorForm",
          "pc-form",
          submitButtom.innerText
        );

        // remove stepper and show complete header
        {
          pcWrapper.querySelectorAll(".complete").forEach((f) => {
            f.removeAttribute("hidden");

            setTimeout(() => {
              f.querySelectorAll("svg").forEach((f1) => {
                f1.classList.add("active");
              });
            }, 25);
          });

          pcWrapper.querySelectorAll(".stepper").forEach((f) => {
            f.setAttribute("hidden", "");
          });
        }

        pcState.gotoStep(5);
      });
    });
  });
}
